<script lang="ts">
  // The single prompt/confirm <dialog class="tik-dlg"> driven by the `dialog` store (spec §16).
  // Enter submits, Escape cancels, a click outside the panel cancels; the prompt OK button is
  // disabled while the value is empty.
  import { t } from '$lib/i18n';
  import { dialog } from '$lib/store';

  let el = $state<HTMLDialogElement | null>(null);
  let input = $state<HTMLInputElement | null>(null);
  let value = $state('');

  const spec = $derived(dialog.spec);
  const isPrompt = $derived(spec?.kind === 'prompt');

  $effect(() => {
    const s = spec;
    const d = el;
    if (!d) return;
    if (s) {
      value = s.value ?? '';
      if (!d.open) {
        if (typeof d.showModal === 'function') d.showModal();
        else d.setAttribute('open', '');
      }
      if (s.kind === 'prompt') {
        requestAnimationFrame(() => {
          input?.focus();
          input?.select();
        });
      }
    } else if (d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
    }
  });

  function submit(ev: SubmitEvent) {
    ev.preventDefault();
    if (!spec) return;
    if (spec.kind === 'confirm') dialog.submit();
    else dialog.submit(value);
  }

  function onCancelEvent(ev: Event) {
    ev.preventDefault();
    if (dialog.spec) dialog.cancel();
  }

  function onClose() {
    if (dialog.spec) dialog.cancel();
  }

  /** A click on the backdrop lands on the <dialog> itself (outside its content box). */
  function onClick(ev: MouseEvent) {
    if (!el || ev.detail === 0 || !spec) return;
    if (ev.target !== el) return;
    const r = el.getBoundingClientRect();
    const inside = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
    if (!inside) dialog.cancel();
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
<dialog class="tik-dlg" bind:this={el} oncancel={onCancelEvent} onclose={onClose} onclick={onClick} aria-labelledby="tik-dlg-title">
  {#if spec}
    <form onsubmit={submit}>
      <h3 id="tik-dlg-title">{spec.title}</h3>
      {#if spec.body}<p class="dlg-body">{spec.body}</p>{/if}
      {#if isPrompt}
        <input class="dlg-input" aria-labelledby="tik-dlg-title" enterkeyhint="done" autocomplete="off" spellcheck="false" placeholder={spec.placeholder ?? ''} bind:value bind:this={input} />
      {/if}
      <div class="dlg-actions">
        <button type="button" class="dlg-btn cancel" onclick={() => dialog.cancel()}>{t('cancel')}</button>
        <button type="submit" class="dlg-btn ok" class:danger={!!spec.danger} disabled={isPrompt && !value.trim()}>{spec.ok ?? t('ok')}</button>
      </div>
    </form>
  {/if}
</dialog>

<style>
  /* @css shell/Dialog start */
  .tik-dlg {
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-panel);
    padding: var(--space-5);
    width: min(400px, calc(100vw - var(--space-8)));
    max-width: none;
    background: var(--surface-card);
    color: var(--text-body);
    box-shadow: var(--shadow-dialog)
  }

  .tik-dlg::backdrop {
    background: var(--scrim)
  }

  .tik-dlg h3 {
    font-size: var(--text-lg);
    font-weight: var(--weight-semibold);
    color: var(--text-strong);
    margin: 0 0 var(--space-2);
    line-height: var(--leading-tight)
  }

  .dlg-body {
    color: var(--text-muted);
    font-size: var(--text-sm);
    line-height: var(--leading-body);
    margin: 0 0 var(--space-4)
  }

  .dlg-input {
    width: 100%;
    block-size: var(--control-lg);
    padding-inline: var(--space-3);
    margin-bottom: var(--space-5);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-page);
    color: var(--text-strong);
    font-size: var(--text-sm);
    line-height: var(--leading-tight)
  }

  .dlg-input:focus-visible {
    outline: none;
    border-color: var(--focus-border);
    box-shadow: var(--ring-focus)
  }

  .dlg-actions {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end
  }

  .dlg-btn {
    min-block-size: var(--control-lg);
    padding-inline: var(--space-5);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm)
  }

  .cancel {
    color: var(--text-body)
  }

  .cancel:hover {
    background: var(--surface-subtle)
  }

  .cancel:active {
    background: var(--surface-sunken)
  }

  .ok {
    background: var(--mail-accent);
    color: var(--text-on-accent)
  }

  .ok:hover {
    background: var(--mail-accent-hover)
  }

  .ok:active {
    background: var(--blue-700)
  }

  .ok.danger {
    background: var(--danger-500)
  }

  .ok.danger:hover {
    filter: brightness(1.08)
  }

  .ok.danger:active {
    filter: brightness(.92)
  }

  .ok:disabled {
    opacity: .5;
    cursor: default
  }

  input {
    block-size: var(--control-lg);
    padding: 0 var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-card);
    color: var(--text-body);
    width: 100%;
    transition: border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)
  }

  input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .dlg-actions {
    display: flex;
    gap: var(--space-2);
    justify-content: flex-end
  }

  @media (pointer:fine) {
  .form {
      scrollbar-gutter: stable
    }
  }

  .tik-dlg h3 {
    text-wrap: balance
  }

  @media (max-width:480px) {
  .form {
      padding-bottom: calc(2 * var(--control-lg) + var(--space-4) + env(safe-area-inset-bottom))
    }
  }
  /* @css shell/Dialog end */
</style>
